"use client";

// Voice-announcement orchestrator.
//
// One instance lives at the top of the map view. It:
//   1. Registers built-in AnnouncementRules into a RuleEngine.
//   2. Reads preferences from localStorage and applies them to the
//      engine (enabled flag + per-rule params).
//   3. Subscribes to /api/sse/group via useSSE and routes events
//      through engine.onEvent.
//   4. Tracks the latest `server.time` snapshot (rebasing capturedAtMs
//      to the browser clock, mirroring HeaderDayNight) so the day-night
//      rule has something to interpolate from.
//   5. Ticks once per second and runs engine.onTick.
//   6. Speaks every returned Instruction through useSpeechSynthesis,
//      honouring the user's master enabled flag, voice, and volume.
//
// Preference changes (from the VoiceSettings modal in the same tab,
// or from another tab) reach us via the standard `storage` event plus
// a custom `rust-sight:voice-prefs-changed` event that VoiceSettings
// dispatches after each write.

import { useEffect, useRef } from "react";
import { useSSE } from "@/hooks/useSSE";
import { useSpeechSynthesis, pickDefaultVoice } from "@/hooks/useSpeechSynthesis";
import { RuleEngine } from "@/lib/voice/engine";
import { dayNightWarning } from "@/lib/voice/rules/day-night";
import {
  readVoicePrefs,
  VOICE_PREFS_KEY,
  type VoicePreferences,
} from "@/lib/voice/preferences";
import type { AnnouncementContext } from "@/lib/voice/types";
import type { AppTimeSnapshot } from "@/lib/time/day-night";
import type { RealtimeEvent } from "@/lib/realtime/events";

/** Event name VoiceSettings dispatches on window after writing prefs. */
export const VOICE_PREFS_CHANGED_EVENT = "rust-sight:voice-prefs-changed";

export interface UseVoiceAnnouncerOptions {
  /** The user's bound serverKey — only events for this server matter. */
  serverKey: string | null;
}

export function useVoiceAnnouncer({ serverKey }: UseVoiceAnnouncerOptions): void {
  const { speak } = useSpeechSynthesis();

  // Stable engine instance and preference ref so the SSE callback +
  // tick don't re-create them on every render.
  const engineRef = useRef<RuleEngine | null>(null);
  if (engineRef.current === null) {
    const engine = new RuleEngine();
    engine.register(dayNightWarning);
    engineRef.current = engine;
  }
  const prefsRef = useRef<VoicePreferences>(readVoicePrefs());
  const serverTimeRef = useRef<AppTimeSnapshot | null>(null);

  // Apply prefs to the engine on mount + whenever prefs change.
  useEffect(() => {
    const apply = () => {
      const next = readVoicePrefs();
      prefsRef.current = next;
      const engine = engineRef.current;
      if (!engine) return;
      for (const r of engine.listRules()) {
        const rulePref = next.rules[r.id];
        engine.setEnabled(r.id, rulePref?.enabled ?? r.enabled);
        engine.setRuleParams(r.id, rulePref?.params ?? {});
      }
    };
    apply();

    const onStorage = (ev: StorageEvent) => {
      if (ev.key === VOICE_PREFS_KEY || ev.key === null) apply();
    };
    const onCustom = () => apply();
    window.addEventListener("storage", onStorage);
    window.addEventListener(VOICE_PREFS_CHANGED_EVENT, onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(VOICE_PREFS_CHANGED_EVENT, onCustom);
    };
  }, []);

  // Resolve voiceURI lazily — if the user hasn't chosen one, pick the
  // best available match each time we speak (the voice list may
  // populate asynchronously after the first render).
  const speakInstruction = useRef<((text: string) => void) | null>(null);
  speakInstruction.current = (text: string) => {
    const prefs = prefsRef.current;
    if (!prefs.enabled) return;
    let voiceURI = prefs.voiceURI;
    if (!voiceURI && typeof window !== "undefined" && window.speechSynthesis) {
      const v = pickDefaultVoice(window.speechSynthesis.getVoices());
      voiceURI = v?.voiceURI ?? null;
    }
    speak(text, { voiceURI, volume: prefs.volume });
  };

  // SSE subscription. Per-event work:
  //   - server.time → rebase capturedAtMs to browser clock and stash
  //     for the tick loop (mirrors HeaderDayNight).
  //   - anything else → fan into the engine in case a future
  //     event-driven rule (deaths, chat-mentions) cares.
  useSSE("/api/sse/group", (event: RealtimeEvent) => {
    const engine = engineRef.current;
    if (!engine) return;

    if (event.type === "server.time" && serverKey && event.payload.serverKey === serverKey) {
      const snap = event.payload.time;
      if (snap && typeof snap === "object") {
        const rebased: AppTimeSnapshot = {
          ...(snap as AppTimeSnapshot),
          capturedAtMs: Date.now(),
        };
        serverTimeRef.current = rebased;
      }
    }

    const ctxBase: Omit<AnnouncementContext, "ruleParams"> = {
      nowMs: Date.now(),
      serverTime: serverTimeRef.current,
    };
    for (const inst of engine.onEvent(event, ctxBase)) {
      speakInstruction.current?.(inst.text);
    }
  });

  // 1-second tick. Same cadence as HeaderDayNight's countdown render.
  useEffect(() => {
    const id = setInterval(() => {
      const engine = engineRef.current;
      if (!engine) return;
      const ctxBase: Omit<AnnouncementContext, "ruleParams"> = {
        nowMs: Date.now(),
        serverTime: serverTimeRef.current,
      };
      for (const inst of engine.onTick(ctxBase)) {
        speakInstruction.current?.(inst.text);
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);
}
